/**
 * Analytics page - embedded Grafana dashboards for deep analysis
 */
import { useState } from 'react';
import { GrafanaEmbed } from '../components/GrafanaEmbed';
import { usePortfolioFilter } from '../contexts';
import { useTags } from '../hooks/useTags';
import { GRAFANA_URL } from '../utils/grafanaEmbed';
import type { DashboardType } from '../utils/grafanaEmbed';

type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d' | '90d' | '1y';

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: '1h', label: '1 Hour' },
  { value: '6h', label: '6 Hours' },
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '1y', label: '1 Year' },
];

export function AnalyticsPage() {
  const [activeDashboard, setActiveDashboard] = useState<DashboardType>('portfolio-overview');
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const { selectedPortfolioId } = usePortfolioFilter();
  const { data: tags } = useTags();

  // Build params for Grafana embed
  const embedParams = {
    from: `now-${timeRange}`,
    to: 'now',
    portfolio: selectedPortfolioId || undefined,
    tags: selectedTags.length > 0 ? selectedTags.join(',') : undefined,
    refresh: '5m',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <a
          href={GRAFANA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          Open in Grafana
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>

      {/* Dashboard tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 overflow-x-auto">
          <button
            onClick={() => setActiveDashboard('portfolio-overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeDashboard === 'portfolio-overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveDashboard('performance')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeDashboard === 'performance'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Performance
          </button>
          <button
            onClick={() => setActiveDashboard('risk-volatility')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeDashboard === 'risk-volatility'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Risk & Volatility
          </button>
          <button
            onClick={() => setActiveDashboard('watchlist')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeDashboard === 'watchlist'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Watchlist
          </button>
          <button
            onClick={() => setActiveDashboard('price-history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeDashboard === 'price-history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Price History
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Range
            </label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              {TIME_RANGES.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tag Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <select
              multiple
              value={selectedTags}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
                setSelectedTags(selected);
              }}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              size={3}
            >
              {tags?.map((t) => (
                <option key={t.tag_id} value={t.tag_id}>
                  {t.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSelectedTags([]);
              }}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear Tags
            </button>
          </div>
        </div>
      </div>

      {/* Grafana Dashboard Embed */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <GrafanaEmbed
          dashboard={activeDashboard}
          params={embedParams}
          height="calc(100vh - 350px)"
          className="min-h-[500px]"
        />
      </div>

      {/* Help text */}
      <div className="text-sm text-gray-500 text-center">
        <p>
          Dashboards are powered by Grafana. For advanced filtering and custom time ranges,{' '}
          <a
            href={GRAFANA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            open Grafana directly
          </a>
          .
        </p>
      </div>
    </div>
  );
}

export default AnalyticsPage;
